import { ArrowLeftOutlined } from "@ant-design/icons"
import { attachmentPlugin } from "@react-pdf-viewer/attachment"
import { type RenderError, type SpecialZoomLevel, Viewer } from "@react-pdf-viewer/core"
import "@react-pdf-viewer/core/lib/styles/index.css"
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation"
import { toolbarPlugin } from "@react-pdf-viewer/toolbar"
import "@react-pdf-viewer/toolbar/lib/styles/index.css"
import React, { useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { AttachmentFileViewer } from "~Components/fileViewer/AttachmentFileViewer"
import { customCanvasPlugin } from "~Components/fileViewer/customCanvasPlugin"
import { FileViewerToolbar } from "~Components/fileViewer/FileViewerToolbar"
import { type AttachmentItem, useAttachmentPlugin } from "~Shared/hooks/fileViewer.hooks"

export interface FileViewerProps {
    filePath: string
    isXmlEInvoice?: boolean
    showPaidStamp?: boolean
    RightSideToolbar?: React.ReactNode | null
    onUnsupportedFormatDownload?: () => void
    renderError?: RenderError
    pdfHeight?: number | "auto"
    enableSmoothScroll?: boolean
    defaultScale?: SpecialZoomLevel | number
    showToolbar?: boolean
}

export const FileViewer = React.memo(
    ({
        filePath,
        showPaidStamp = false,
        RightSideToolbar,
        renderError,
        onUnsupportedFormatDownload,
        isXmlEInvoice = false,
        pdfHeight = 900,
        enableSmoothScroll = false,
        defaultScale = 1,
        showToolbar = true,
    }: FileViewerProps) => {
        const { t } = useTranslation()

        const [currentFileUrl, setCurrentFileUrl] = useState<string>(filePath)
        const [isViewingAttachment, setIsViewingAttachment] = useState(false)
        const [attachments, setAttachments] = useState<Array<AttachmentItem>>([])
        const [isExpanded, setIsExpanded] = useState(false)

        const { current: pageNavigationPluginInstance } = useRef(pageNavigationPlugin())
        const toolbarPluginInstance = toolbarPlugin({})
        const attachmentPluginInstance = attachmentPlugin()
        const customAttachmentPluginInstance = useAttachmentPlugin(setAttachments)

        const isViewableAttachment = (filename: string): boolean => {
            const extension = filename.toLowerCase().split(".").pop()
            return extension === "pdf"
        }

        const viewableAttachments = useMemo(() => attachments.filter((att) => isViewableAttachment(att.name)), [attachments])

        if (!filePath) {
            return null
        }

        return (
            <div
                className="border-none flex flex-col w-full"
                style={{
                    height: viewableAttachments.length > 0 ? Number(pdfHeight) + viewableAttachments.length * 80 : pdfHeight,
                }}
            >
                {showToolbar ? (
                    <FileViewerToolbar
                        isXmlEInvoice={isXmlEInvoice}
                        RightSideToolbar={RightSideToolbar}
                        onUnsupportedFormatDownload={onUnsupportedFormatDownload}
                        pageNavigationPluginInstance={pageNavigationPluginInstance}
                        toolbarPluginInstance={toolbarPluginInstance}
                    />
                ) : null}
                <div className="flex-1 overflow-hidden">
                    {/* show back button when viewing attachment */}
                    {isViewingAttachment && (
                        <div className="p-8">
                            <button
                                className=" border-none p-2 rounded cursor-pointer font-bold gap-8"
                                onClick={() => {
                                    setCurrentFileUrl(filePath)
                                    setIsViewingAttachment(false)
                                }}
                                type="button"
                            >
                                <ArrowLeftOutlined /> Back to main document
                            </button>
                        </div>
                    )}

                    {/* attachment list */}
                    {!isViewingAttachment && (
                        <AttachmentFileViewer
                            viewableAttachments={viewableAttachments}
                            onExpandChange={() => setIsExpanded(!isExpanded)}
                            setCurrentFileUrl={setCurrentFileUrl}
                            setIsViewingAttachment={setIsViewingAttachment}
                        />
                    )}

                    {/* https://github.com/react-pdf-viewer/react-pdf-viewer/issues/1076
                ugly hack to mitigate the re-rendering issue. TODO replace it after getting an answer from the library maintainer */}
                    <Viewer
                        key={currentFileUrl}
                        fileUrl={showPaidStamp ? currentFileUrl : ` ${currentFileUrl}`}
                        plugins={[
                            customAttachmentPluginInstance,
                            attachmentPluginInstance,
                            toolbarPluginInstance,
                            pageNavigationPluginInstance,
                            ...(showPaidStamp ? [customCanvasPlugin(t("label:paid"))] : []),
                        ]}
                        renderError={renderError}
                        enableSmoothScroll={enableSmoothScroll}
                        defaultScale={defaultScale}
                    />
                </div>
            </div>
        )
    },
)
